import * as React from 'react';
import DocumentHeader from '../../../components/DocumentHeader';
import DocumentWrapper from '../../../components/DocumentWrapper';
import PlatformConditional from '../../../components/PlatformConditional';
import FormEditingHandler from '../../../containers/FormEditingHandler';
import { filterDocs, getHeaderId } from '../utils';
import { TDocument } from './definitions';
import Form from './Form';

import MOCAHistory from './HistoryRowData/MOCA';
import MMSEHistory from './HistoryRowData/MMSE';
import TMTHistory from './HistoryRowData/TMT';
import SDMTHistory from './HistoryRowData/SDMT';
import CDRHistory from './HistoryRowData/CDR';
import CERADHistory from './HistoryRowData/CERAD';

const CognitionHistory = (
  documents: Array<TDocument>,
  startEdit: (document: TAnyObject) => (e: React.MouseEvent<Element, MouseEvent>) => void,
  view?: IView,
): React.JSX.Element => {
  const MOCADocs = filterDocs('moca', documents);
  const MMSEDocs = filterDocs('mmse', documents);
  const TMTDocs = filterDocs('tmt', documents);
  const SDMTDocs = filterDocs('sdmt', documents);
  const CDRDocs = filterDocs('cdr', documents);
  const CERADDocs = filterDocs('cerad', documents);

  const BackgroundDocs = filterDocs('background', documents);

  return (
    <React.Fragment>
      <PlatformConditional platform="parkinson">
        <React.Fragment>
          <MOCAHistory documents={MOCADocs as IMOCA[]} startEdit={startEdit} view={view} />
          <MMSEHistory documents={MMSEDocs as IMMSE[]} startEdit={startEdit} view={view} />
          <CERADHistory documents={[...CERADDocs, ...BackgroundDocs]} startEdit={startEdit} view={view} />
          <TMTHistory documents={TMTDocs as ITMT[]} startEdit={startEdit} view={view} />
          <CDRHistory documents={CDRDocs as ICDR[]} startEdit={startEdit} view={view} />
        </React.Fragment>
      </PlatformConditional>
      <PlatformConditional platform={['parkinson', 'ms', 'huntington']}>
        <SDMTHistory documents={SDMTDocs as ISDMT[]} startEdit={startEdit} view={view} />
      </PlatformConditional>
    </React.Fragment>
  );
};

const Cognition = ({ documents }: IOwnProps): React.JSX.Element => {
  const editingDocumentType = (id: string) => documents.find(doc => doc._id === id)?._type;
  return (
    <FormEditingHandler name="cognition" documents={documents}>
      {(editing, startEdit, formData, view): React.JSX.Element => (
        <DocumentWrapper>
          <DocumentHeader
            name={'cognition'}
            headerId={getHeaderId(documents, editing, view?.viewing)}
            headerWidth={8}
            editing={editing}
            editButtons={<div />}
            infoText={
              editing &&
              editingDocumentType(editing) &&
              'cognition.' + editingDocumentType(editing) + '.meterDescriptionText'
            }
          />
          {editing ? (
            <Form
              documents={documents}
              formData={formData}
              document={documents && documents.find((d: TDocument) => d._id === editing)}
            />
          ) : null}
          {view?.viewing ? (
            <Form
              documents={documents}
              formData={{ document: view.document, onChange: (): string => '' }}
              document={documents && documents.find((d: TDocument) => d._id === view?.viewing)}
              viewing={!!view.viewing}
            />
          ) : null}
          {!editing && !view?.viewing ? CognitionHistory(documents, startEdit, view) : undefined}
        </DocumentWrapper>
      )}
    </FormEditingHandler>
  );
};

interface IOwnProps {
  documents: Array<TDocument>;
}

export default Cognition;
